/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { DropdownModel } from '../../../../../../core/models';
import { Injectable } from '@angular/core';
import { LanguageService, LogService, UtilsService } from '../../../../../../core/services';
import { Subject } from 'rxjs';
import { TemplateModel } from '../../../models';
import { WizardStepModel } from '../../../models/wizardStep.model';
import { WizardTemplateService } from './wizardTemplate.service';
import { WizardUtilsService } from './wizardUtils.service';

@Injectable()
export class WizardService {
    /**
     * Section is valid set next button as active.
     */
    public isSectionValidEmitter: Subject<boolean> = new Subject<boolean>();

    /**
     * Emits the parent step section name and index.
     */
    public parentStepEmitter: Subject<any> = new Subject<any>();

    /**
     * Emits the sub step title.
     */
    public subStepNameEmitter: Subject<any> = new Subject<any>();

    /**
     * Emits the wizard mode dropdown.
     */
    public wizardModeDropdownEmitter: Subject<boolean> = new Subject<boolean>();

    /**
     * Emits the parent step section name and index.
     */
    public wizardModeEmitter: Subject<any> = new Subject<any>();

    /**
     * Emits the new wizardSteps object.
     */
    public wizardStepObjectEmitter: Subject<any> = new Subject<any>();

    /**
     * Notify others that the error notification has been closed.
     */
    public errorNotificationClosed: Subject<any> = new Subject<any>();

    /**
     * Notify others that the last step/sub-step in a section has been reached.
     */
    public isLastSectionInService: Subject<boolean> = new Subject<boolean>();

    /**
     * Showing the loading indicator.
     */
    public isLoading: boolean = true;

    /**
     * Where we are in the wizard.
     */
    public storedSubStepPosition: number | 'first' | 'last';

    /**
     * Store the dropdown content for use in the view.
     */
    public dropdown = {
        wizardMode: []
    };

    /**
     * Store the state of the wizard submission process.
     */
    public submissionActive: boolean = false;

    /**
     * Message to show when adding/editing a service.
     */
    public wizardProgressMessage: string;

    /**
     * Store the errored steps if any.
     */
    public erroredSteps: any[] = [];

    /**
     * Local reference to advanced mode.
     * @private
     */
    private _wizardMode: string = 'default';

    /**
     * Disable navigation from external source if embedded component is loaded.
     * @private
     */
    private _disableNavigation: boolean = false;

    /**
     * Wizard steps setter and getter private variable.
     * @private
     */
    private _wizardSteps: any[] = [];

    /**
     * Selected template stored after selection.
     * @private
     */
    private _selectedTemplate: TemplateModel = null;

    /**
     * Stored controller state, values and visibility.
     * @private
     */
    private _controllerState: any = {};

    /**
     * Where we are in the wizard parent steps.
     */
    public _activeStepIndex: number = 0;

    /**
     * Where we are in the wizard sub steps.
     */
    public _activeSubStepIndex: number = 0;

    /**
     * Selected wizard mode to set selected key on wizard mode dropdown.
     * @private
     */
    private _wizardSelectedMode: string;

    /**
     * Used to stop navigating back from the first step.
     * @private
     */
    private _isFirstStep: boolean = true;

    /**
     * Used to stop navigating forward from the last step.
     * Switches the last step button from next to submit.
     * @private
     */
    private _isLastStep: boolean = false;

    /**
     * Allow component to register themselves to not show the 'add another' service at the end of the component.
     * @private
     */
    private _excludeFromAddAnother: any[] = [];

    /**
     * A list of wizard subscribers to destroy.
     * @private
     */
    private wizardSubscribers: any = [];

    /**
     * Store temp ids for all services.
     */
    public tempIds: string[] = [];

    constructor(
        private languageService: LanguageService,
        private logService: LogService,
        private utilsService: UtilsService,
        private wizardTemplateService: WizardTemplateService,
        private wizardUtilsService: WizardUtilsService,
    ) {
        this.setupWizardSubscribers();
    }

    /**
     * Get wizard mode.
     */
    get wizardMode(): string {
        return this._wizardMode;
    }

    /**
     * Set wizard mode.
     * @param mode
     */
    set wizardMode(mode: string) {
        this._wizardMode = mode;
    }

    /**
     * Get wizard mode.
     */
    get disableNavigation(): boolean {
        return this._disableNavigation;
    }

    /**
     * Set wizard mode.
     * @param state
     */
    set disableNavigation(state: boolean) {
        this._disableNavigation = state;
    }

    /**
     * Get wizard steps.
     */
    get wizardSteps(): any[] {
        return this._wizardSteps;
    }

    /**
     * Set wizard steps.
     * @param wizardSteps
     */
    set wizardSteps(wizardSteps) {
        this._wizardSteps = wizardSteps;
    }

    /**
     * Get selected template.
     */
    get selectedTemplate(): TemplateModel {
        return this._selectedTemplate;
    }

    /**
     * Set selected template.
     * @param template
     */
    set selectedTemplate(template: TemplateModel) {
        this._selectedTemplate = template;
        this.wizardTemplateService.templateChangeEmitter.next(this.selectedTemplate);
    }

    /**
     * Get controller state.
     */
    get controllerState(): any {
        return this._controllerState;
    }

    /**
     * Set controller state.
     * @param state
     */
    set controllerState(state: any) {
        this._controllerState = state;
    }

    /**
     * Get active step index.
     */
    get activeStepIndex(): number {
        return this._activeStepIndex;
    }

    /**
     * Set active step index.
     * @param index
     */
    set activeStepIndex(index: number) {
        this._activeStepIndex = index;
    }

    /**
     * Get active sub step index.
     */
    get activeSubStepIndex(): number {
        return this._activeSubStepIndex;
    }

    /**
     * Set active sub step index.
     * @param index
     */
    set activeSubStepIndex(index: number) {
        this._activeSubStepIndex = index;
    }

    /**
     * Get wizard selected mode.
     */
    get wizardSelectedMode(): string {
        return this._wizardSelectedMode;
    }

    /**
     * Set wizard selected mode.
     * @param wizardMode
     */
    set wizardSelectedMode(wizardMode) {
        this._wizardSelectedMode = wizardMode;
    }

    /**
     * Get wizard dropdown modes
     */
    get wizardModeDropdown(): any[] {
        return this.dropdown.wizardMode;
    }

    /**
     * Set wizard dropdown modes
     * @param newModes
     */
    set wizardModeDropdown(newModes) {
        this.dropdown.wizardMode = newModes;
        this.wizardModeDropdownEmitter.next(true);
    }

    /**
     * Get if we are on the first step of not.
     */
    get isFirstStep(): boolean {
        return this._isFirstStep;
    }

    /**
     * Set is first step.
     * @param state
     */
    set isFirstStep(state) {
        this._isFirstStep = state;
    }

    /**
     * Get if this is the last step
     */
    get isLastStep(): boolean {
        return this._isLastStep;
    }

    /**
     * Set if this is the last step
     * @param state
     */
    set isLastStep(state) {
        this._isLastStep = state;
    }

    set excludeFromAddAnother(serviceName: string) {
        this._excludeFromAddAnother.push(serviceName);
    }

    get excludeFromAddAnother(): string[] {
        return this._excludeFromAddAnother;
    }

    /**
     * Get previous step index.
     * @param steps
     * @param activeIndex
     */
    protected static getPreviousIndex(steps: string | any[], activeIndex: number): number {
        const currentId = steps[activeIndex].id;
        // This needs to be in reverse, so we can't use other array methods.
        for (let index = steps.length - 1; index >= 0; index--) {
            const item = steps[index];
            if (index <= activeIndex && item.id !== currentId && item.isVisible) {
                return index;
            }
        }

        return activeIndex;
    }

    /**
     * Set first step when wizard loads.
     */
    public setFirstStepActive(): void {
        if (this.wizardSteps.length) {
            this.activeStepIndex = 0;
            const firstStep = this.wizardSteps[this.activeStepIndex];
            firstStep.isActive = true;
            this.emitParentStep(firstStep.service, firstStep.serviceIndex);

            if (firstStep.subSteps.length) {
                const firstSubStep = firstStep.subStep[0];
                firstSubStep.isActive = true;
                this.activeSubStepIndex = 0;
                this.subStepNameEmitter.next(firstSubStep.name);
            }

            if (firstStep.id !== 'templateSelection') {
                this.checkForFirstAndLastStep();
            }
        }
    }

    /**
     * Add sub steps from loaded component so the steps show in the navigation.
     * @param subStepTitles
     * @param subStepInputsByTitle
     * @param inputStateStore
     */
    public registerSubStepsWithState(subStepTitles: any[], subStepInputsByTitle: any, inputStateStore: any): void {
        // Assign the active step sub steps.
        const step = this.getStepById(this.wizardSteps, this.wizardSteps[this.activeStepIndex].id);
        step.subSteps = this.createSubSteps(subStepTitles, subStepInputsByTitle, inputStateStore);

        // Check to see if there is a stored position, if so set it as the active sub step index.
        const visibleSubSteps = this.filterVisibleSteps(step.subSteps);

        this.activeSubStepIndex = this.storedSubStepPosition && typeof this.storedSubStepPosition !== 'number'
            ? this.convertPositionToIndex(this.storedSubStepPosition, visibleSubSteps)
            : this.activeSubStepIndex || 0;

        // Set the navigation to the correct step.
        const parentIndex = this.wizardSteps.findIndex(innerStep => innerStep.id === step.id);
        const activeSubSteps = this.wizardSteps[parentIndex].subSteps;
        const subStepsIndex = activeSubSteps.findIndex((innerStep: any) => innerStep.id === visibleSubSteps[this.activeSubStepIndex].id);

        // Set the navigation state.
        this.updateStepStates(parentIndex, subStepsIndex);

        this.subStepNameEmitter.next(activeSubSteps[subStepsIndex].name);
        this.wizardStepObjectEmitter.next(this.wizardSteps);

        // Report if last step in section after the sub-steps have been registered.
        this.isLastSectionInService.next(this.isLastStepSection(step));
    }

    /**
     * Create sub steps from component values.
     * @param subStepTitles
     * @param subStepInputsByTitle
     * @param inputStateStore
     */
    public createSubSteps(subStepTitles: any[], subStepInputsByTitle: any, inputStateStore: any): any[] {
        const subSteps = [];

        for (const subStepTitle of subStepTitles) {
            const subStepInputs = subStepInputsByTitle[subStepTitle];

            // If there are inputs associated to the sub step title push them to the subSteps array.
            if (subStepInputs && subStepInputs.length) {
                subSteps.push({
                    id: this.utilsService.generateRandomString(),
                    name: subStepTitle,
                    title: subStepTitle,
                    isActive: false,
                    isComplete: false,
                    isVisible: subStepInputs.some((input: string | number) => inputStateStore[input].hidden === false),
                });
            }
        }

        return subSteps;
    }

    /**
     * Toggle the visible state of a sub step by section name.
     * Used in the components to kick changes to the navigation.
     * @param name
     * @param state
     */
    public setSubStepVisibleState(name: string, state: boolean): void {
        const stepsTimeout = setTimeout((): void => {
            const activeStepSubSteps = this.wizardSteps[this.activeStepIndex] && this.wizardSteps[this.activeStepIndex].subSteps;
            if (activeStepSubSteps) {
                const subStepIndex = activeStepSubSteps.findIndex((subStep: {
                    name: string;
                }): boolean => subStep.name === name);

                if (subStepIndex !== -1) {
                    activeStepSubSteps[subStepIndex].isVisible = state;
                    if (!state && activeStepSubSteps[subStepIndex].isActive) {
                        this.navigateToLastVisibleStep(activeStepSubSteps);
                        // If we have navigated away set active state to false.
                        activeStepSubSteps[subStepIndex].isActive = false;
                    }
                }

                this.checkForFirstAndLastStep();

                this.wizardStepObjectEmitter.next(this.wizardSteps);
            } else {
                this.logService.warn(
                    'WizardService: setSubStepVisibleState',
                    'There was an issue getting activeStepSubSteps.'
                );
            }

            clearTimeout(stepsTimeout);
        }, 0);
    }

    /**
     * Used by components to check if the active step has sub steps.
     */
    public activeStepHasSubSteps(): boolean {
        const activeStep = this.wizardSteps[this.activeStepIndex];

        return activeStep.subSteps && !!activeStep.subSteps.length;
    }

    /**
     * Move to previous step.
     */
    public previousStep(): void {
        this.isLastStep = false;
        const currentSubSteps: any = this.wizardSteps[this.activeStepIndex].subSteps;

        if (this.decrementSubStep(currentSubSteps)) {
            const newSubStepIndex: number = WizardService.getPreviousIndex(currentSubSteps, this.activeSubStepIndex);
            this.goToStep(this.activeStepIndex, newSubStepIndex);
        } else {
            const previousStepIndex: number = WizardService.getPreviousIndex(this.wizardSteps, this.activeStepIndex);
            const newSubStepIndex: number | 'first' | 'last' = this.getPreviousStepSubStepIndex(previousStepIndex);
            this.goToStep(previousStepIndex, newSubStepIndex);
        }
        this.wizardUtilsService.scrollActiveInView();
    }

    /**
     * Move to next step.
     */
    public nextStep(): void {
        this.isFirstStep = false;
        const currentSubSteps: any = this.wizardSteps[this.activeStepIndex].subSteps;

        if (this.incrementSubStep(currentSubSteps)) {
            const newSubStepIndex: number = this.getNextIndex(currentSubSteps, this.activeSubStepIndex);
            this.goToStep(this.activeStepIndex, newSubStepIndex);
        } else {
            const nextStepIndex: number = this.getNextIndex(this.wizardSteps, this.activeStepIndex);
            const newSubStepIndex: number = this.getNextStepSubStepIndex(nextStepIndex);
            this.goToStep(nextStepIndex, newSubStepIndex);
        }

        this.wizardUtilsService.scrollActiveInView();
    }

    /**
     * Create template selection step.
     */
    public createTemplateSelectionStep(): void {
        this.wizardSteps = [{
            id: 'templateSelection',
            title: 'Template Selection',
            service: 'templateSelection',
            isActive: true,
            isComplete: false,
            isVisible: true,
            subSteps: []
        }];

        // Tell the wizard to draw the step navigation.
        this.wizardStepObjectEmitter.next(this.wizardSteps);
    }

    /**
     * Go to the template selection step.
     * @param step
     */
    public goToTemplateSelector(step: { service: any; }): void {
        this.updateStepStates(0, 0);
        this.emitParentStep(step.service, 0);
    }

    /**
     * Navigate to a specific sub step of a given parent step.
     * @param parentStep
     * @param subStep
     */
    public goToSubStep(parentStep: { subSteps: any[]; }, subStep: { isComplete: any; id: any; name: any; }): void {
        if (subStep.isComplete) {
            this.updateSubStepStates(parentStep, parentStep.subSteps.findIndex(innerSubStep => innerSubStep.id === subStep.id));
            this.subStepNameEmitter.next(subStep.name);
            this.checkForFirstAndLastStep();
        }
    }

    /**
     * Go to step in the wizard (only visible steps)
     * @param stepPosition
     * @param subStepPosition
     * @param forceEmitter - by default the step will not be emitted if the index hasn't changed.
     */
    public goToStep(
        stepPosition: number | 'first' | 'last',
        subStepPosition?: number | 'first' | 'last',
        forceEmitter: boolean = false
    ): void {
        // Deal with parent steps.
        const wizardStepsParentIndex: number = this.getNewParentStepIndex(stepPosition);
        const newParentStep = this.getNewParentStep(wizardStepsParentIndex, stepPosition, forceEmitter);
        // Deal with sub steps.
        const wizardSubStepIndex: number = this.getNewSubStepIndex(newParentStep, subStepPosition);
        this.updateStepStates(wizardStepsParentIndex, wizardSubStepIndex);
        this.wizardUtilsService.scrollActiveInView();
        // Report if last step in section
        this.isLastSectionInService.next(this.isLastStepSection(newParentStep));
    }

    /**
     * Helper function to set up wizard states.
     */
    public createServiceStates(): void {
        const clonedTemplate: any = this.utilsService.duplicateObject(this.selectedTemplate);
        const objKeys: string[] = Object.keys(this.controllerState);
        if (!objKeys.length) {
            this.createWizardStepStateFromTemplate(clonedTemplate);
        }
        this.createControllerState(clonedTemplate);

        if (clonedTemplate.wizardStartPosition) {
            if (Array.isArray(clonedTemplate.wizardStartPosition)) {
                const step: any = clonedTemplate.wizardStartPosition[0];
                const subStep: any = clonedTemplate.wizardStartPosition[1];
                this.goToStep(step, subStep);
            } else {
                this.goToStep(clonedTemplate.wizardStartPosition, null);
            }
        } else {
            this.nextStep();
        }
    }

    /**
     * Create wizard state from template.
     * @param template
     */
    public createWizardStepStateFromTemplate(template: TemplateModel): void {
        const templateGroup = this.wizardMode === 'partner' && template.builds ? 'builds' : 'sections';

        for (const groups in template[templateGroup]) {
            if (template[templateGroup].hasOwnProperty(groups)) {
                let index: number = 0;
                for (const service of template[templateGroup][groups]) {
                    const isVisible = this.wizardUtilsService.hasVisibleFields(service);
                    const serviceTitle = service.title || this.utilsService.convertNameToSingular(groups, index);
                    this.wizardSteps.push({
                        id: this.utilsService.generateRandomString(),
                        service: groups,
                        serviceIndex: index,
                        title: serviceTitle,
                        isActive: false,
                        isComplete: false,
                        isVisible: isVisible,
                        subSteps: []
                    });
                    index++;
                }
            }
        }

        // Tell the wizard to draw the step navigation, do this here in case there are no sub steps registered.
        this.wizardStepObjectEmitter.next(this.wizardSteps);
    }

    /**
     * Updates the wizard state when the wizard mode changes.
     * @param newTemplate
     */
    public updateWizardStateFromNewTemplate(newTemplate: TemplateModel): void {
        // Check if wizard has the template selector and then clear out other existing steps.
        const templateSelectionIndex: number = this.wizardSteps.findIndex(innerStep => innerStep.id === 'templateSelection');
        if (templateSelectionIndex !== -1) {
            this.wizardSteps.splice(templateSelectionIndex + 1);
        } else {
            this.wizardSteps = [];
        }
        // Load new steps
        this.createWizardStepStateFromTemplate(newTemplate);
        this.wizardStepObjectEmitter.next(this.wizardSteps);
        const firstVisibleStepIndex: number = this.wizardSteps.findIndex(item => item.isVisible);
        const stepPosition = templateSelectionIndex !== -1 ? firstVisibleStepIndex + 1 : firstVisibleStepIndex;
        // Go to the first step in the new template.
        this.goToStep(stepPosition, 0, true);
    }

    /**
     * Create the controller state that the wizard relies on.
     * @param template
     */
    public createControllerState(template: any): void {
        const sections: any = template.builds ? template.builds : template.sections;
        this.tempIds = [];
        for (const section in sections) {
            if (sections.hasOwnProperty(section)) {
                if (!this.controllerState[section]) {
                    // TODO this will need some work when the backend is built
                    this.createTempIdsForServices(section, sections);
                    this.controllerState[section] = sections[section];
                } else {
                    this.logService.warn(
                        'WizardService: createControllerState',
                        `There was an existing controller state for section ${section}.`
                    );
                }
            }
        }
    }

    /**
     * Create Temp Ids for services without Ids.
     * @param section
     * @param sections
     */
    public createTempIdsForServices(section: string, sections: any): void {
        this.utilsService.createArrayIfNotExist(this.tempIds, section);
        for (const service of sections[section]) {
            this.setIdForAdditionalService(service, service?.id, section);
        }
    }

    /**
     * Create a temp id for the additional service.
     * @param step
     * @private
     */
    private createTempIdForAdditionalService(step: any): void {
        const serviceStep = this.controllerState[step.service];
        const newService = serviceStep[serviceStep.length - 1];
        const section: string = step.service;
        this.utilsService.createArrayIfNotExist(this.tempIds, section);

        this.setIdForAdditionalService(newService, newService?.id?.value, section);
    }

    /**
     * Set the id for the additional service.
     * @param service
     * @param serviceId
     * @param section
     * @private
     */
    private setIdForAdditionalService(service: any, serviceId: string, section: string) {
        if (!serviceId || !this.utilsService.isServiceId(service?.id?.value)) {
            const tempId: string = 'temp-' + Date.now() + Math.random();
            this.tempIds[section].push(tempId);
            service['id'] = { value: tempId, hidden: true };
        }
    }

    /**
     * Add another service.
     * @param currentStep
     * @param nextStep
     * @param section
     */
    public addService(currentStep: any, nextStep = true, section?: any): void {
        const clonedWizardSteps: any = this.utilsService.duplicateObject(this.wizardSteps);
        const stepIndex: number = clonedWizardSteps.findIndex(
            (item: WizardStepModel): boolean =>
                item.serviceIndex === currentStep.index &&
                item.service === currentStep.service
        );

        if (stepIndex === -1) {
            this.logService.debug('WizardService:addService', 'Can\'t match a step to create a new service.');
        } else {
            const step: any = clonedWizardSteps[stepIndex];
            const selectedSection: any = section ? section : this.cloneSectionFromTemplateByStep(step);
            const tempId: string = 'temp-' + Date.now() + Math.random();
            selectedSection['id'] = { value: tempId, hidden: true };
            this.controllerState[step.service].push(selectedSection);

            const clonedStep: WizardStepModel = this.createCloneOfStep(step);
            clonedWizardSteps.splice(stepIndex + 1, 0, clonedStep);

            this.wizardSteps = clonedWizardSteps;
            this.wizardStepObjectEmitter.next(this.wizardSteps);

            if (nextStep) {
                this.nextStep();
            }
        }
    }

    /**
     * Clone section from template based on current step.
     * @param step
     * @private
     */
    private cloneSectionFromTemplateByStep(step: any): any {
        const template: TemplateModel = this.utilsService.duplicateObject(this.selectedTemplate);
        const serviceKey: any = step.service;
        const sections: any =
            template.hiddenSections?.[serviceKey] ??
            template.builds?.[serviceKey] ??
            template.sections?.[serviceKey] ??
            null;

        if (!Array.isArray(sections) || !sections.length) {
            this.logService.debug('WizardService:cloneSectionFromTemplateByStep', 'Can\'t match a template to the supplied step.');

            return null;
        }

        return sections[0];
    }

    /**
     * Create clone of step.
     * @param step
     * @private
     */
    private createCloneOfStep(step: WizardStepModel): WizardStepModel {
        const clonedStep: WizardStepModel = this.utilsService.duplicateObject(step);
        clonedStep.id = this.utilsService.generateRandomString();
        clonedStep.serviceIndex = step.serviceIndex + 1;
        clonedStep.isActive = false;
        clonedStep.subSteps = this.resetSubStepStates(clonedStep.subSteps);

        return clonedStep;
    }

    /**
     * Reset sub step active state.
     * @param subSteps
     * @private
     */
    private resetSubStepStates(subSteps: any): any {
        if (subSteps.length) {
            for (const subStep of subSteps) {
                subStep.isActive = false;
                subStep.isComplete = false;
            }
        }

        return subSteps;
    }

    /**
     * Set up subscribers for items inside the wizard component.
     */
    public setupWizardSubscribers(): void {
        this.wizardSubscribers = this.wizardModeEmitter
            .subscribe(mode => {
                this.wizardMode = mode;
            });
    }

    /**
     * Set up the wizard mode dropdown.
     */
    public setupWizardModesDropdown(template: TemplateModel): any[] {
        // const selectedTemplate = template ? template : this.selectedTemplate;
        if (template.wizardModes) {
            for (const mode of template.wizardModes) {
                if (this.dropdown.wizardMode.findIndex(dropdown => dropdown.key === mode.toLowerCase()) === -1) {
                    this.dropdown.wizardMode.push(<DropdownModel>{
                        key: mode.toLowerCase(),
                        value: mode + ' ' + this.languageService.translate('mode')
                    });
                }
            }

            this.wizardSelectedMode = template.wizardDefaultMode.toLowerCase();
            this.wizardMode = this.wizardSelectedMode;
            this.wizardModeEmitter.next(this.wizardSelectedMode);

            this.dropdown.wizardMode = this.removeRedundantModes(template);

            return this.dropdown.wizardMode;
        }
    }

    /**
     * Clear the submission class level variables.
     */
    public clearSubmissionClassVariables(): void {
        this.submissionActive = false;
        this.wizardProgressMessage = '';
    }

    /**
     * Clear outstanding states.
     */
    public clearOutstandingStates(): void {
        // Make sure the error state object is clear.
        if (this.controllerState?.errorState) {
            this.clearErroredSteps();
            delete this.controllerState?.errorState;
        }

        if (this.controllerState?.success) {
            delete this.controllerState.success;
        }
    }

    /**
     * Reset service class variable ready for next service modal.
     */
    public resetWizardService(): void {
        this.wizardSteps = [];
        this.wizardMode = 'default';
        this.wizardSelectedMode = 'default';
        this.disableNavigation = false;

        this.activeStepIndex = 0;
        this.activeSubStepIndex = 0;

        this.controllerState = {};
        this.isFirstStep = true;
        this.isLastStep = false;
        this.storedSubStepPosition = null;

        this.isLoading = true;

        this.selectedTemplate = null;
        this.wizardTemplateService.templateChangeEmitter.next(this.selectedTemplate);

        if (this.wizardSubscribers) {
            this.wizardSubscribers.unsubscribe();
        }

        this.logService.warn('WizardService: resetWizardService', 'The wizard variables have been reset.');
    }

    /**
     * Get the new parent step index.
     * @param stepPosition
     * @private
     */
    private getNewParentStepIndex(stepPosition: string | number): number {
        let wizardStepsParentIndex: number;
        if (typeof stepPosition === 'number') {
            wizardStepsParentIndex = this.wizardSteps[stepPosition].isVisible
                ? stepPosition : this.getNextIndex(this.wizardSteps, this.activeStepIndex);
        } else {
            wizardStepsParentIndex = this.convertPositionToIndex(stepPosition, this.wizardSteps);
        }

        return wizardStepsParentIndex;
    }

    /**
     * Get the new parent step and emit if it has changed.
     * @param wizardStepsParentIndex
     * @param stepPosition
     * @param forceEmitter
     * @private
     */
    private getNewParentStep(wizardStepsParentIndex: number, stepPosition: number | string, forceEmitter: boolean): any {
        // If the parent step isn't changing don't emit the step change, this stops the component reloading.
        const newParentStep = this.wizardSteps[wizardStepsParentIndex];
        if (stepPosition !== this.activeStepIndex || forceEmitter) {
            this.emitParentStep(newParentStep.service, newParentStep.serviceIndex);
        }

        return newParentStep;
    }

    /**
     * Get new sub step index and emit it if it has changed.
     * @param newParentStep
     * @param subStepPosition
     * @private
     */
    private getNewSubStepIndex(newParentStep: { subSteps: string | any[]; }, subStepPosition: any): number {
        let wizardSubStepIndex = 0;
        if (newParentStep.subSteps && newParentStep.subSteps.length) {

            if (subStepPosition === null) {
                this.logService.warn(
                    'WizardService: Go to step', 'The sub step position was null, index 0 used.'
                );
                wizardSubStepIndex = 0;
            } else if (typeof subStepPosition === 'number') {
                const subStep = newParentStep.subSteps[subStepPosition];
                wizardSubStepIndex = subStep && subStep.isVisible
                    ? subStepPosition : this.getNextIndex(newParentStep.subSteps, this.activeSubStepIndex);
            } else {
                wizardSubStepIndex = this.convertPositionToIndex(subStepPosition, newParentStep.subSteps);
            }

            this.subStepNameEmitter.next(newParentStep.subSteps[wizardSubStepIndex].name);
        } else {
            this.logService.warn(
                'WizardService: goToStep', 'Stored the sub step position as there are no visible sub steps..'
            );
            this.storedSubStepPosition = subStepPosition;
        }

        return wizardSubStepIndex;
    }

    /**
     * When showing and hiding steps you might make the active step hidden, so you should navigate the user to the last visible step.
     * @param activeSteps
     * @private
     */
    private navigateToLastVisibleStep(activeSteps: { name: any; }[]): void {
        this.activeSubStepIndex = this.wizardUtilsService.getLastVisibleStepIndex(activeSteps);
        if (this.activeSubStepIndex) {
            this.updateStepStates(this.activeStepIndex, this.activeSubStepIndex);
            this.subStepNameEmitter.next(activeSteps[this.activeSubStepIndex].name);
        }
    }

    /**
     * Convert position to index.
     * @param position
     * @param steps
     * @private
     */
    private convertPositionToIndex(position: string, steps: any): number {
        if (!steps.length) {
            this.logService.warn('WizardService: convertPositionToIndex', 'No steps to check against.');

            return 0;
        }

        if (position === 'first') {
            // Go to the first step excluding the template selector.
            return steps.findIndex((item: {
                id: string;
                isVisible: any;
            }) => item.id !== 'templateSelection' && item.isVisible);
        }

        if (position === 'last') {
            return this.utilsService.findIndexReversed(steps, 'isVisible');
        }

        if (typeof position === 'string') {
            return this.getStepIndexById(steps, position);
        }
    }

    /**
     * Emit parent step details.
     * @param service
     * @param serviceIndex
     * @private
     */
    private emitParentStep(service: any, serviceIndex: number): void {
        this.isLoading = true; // Set to false in loaded components.
        const name: string = this.utilsService.convertNameToSingular(service);
        this.parentStepEmitter.next({ service: service, index: serviceIndex, name });
    }

    /**
     * Get step by ID.
     * @param steps
     * @param id
     * @private
     */
    private getStepIndexById(steps: any[], id: string): number {
        const step: any = this.getStepById(steps, id);

        return steps.findIndex((innerStep: any): boolean => innerStep.id === step.id);
    }

    /**
     * Loop through ALL the steps and ALL the sub steps to set the correct isActive and isComplete states.
     * @param parentIndex
     * @param subStepIndex
     * @private
     */
    private updateStepStates(parentIndex: number, subStepIndex: number): void {
        this.wizardSteps.forEach((parentStep, parentStepIndex: number): void => {
            if (parentStep.isVisible) {
                parentStep.isActive = parentStepIndex === parentIndex;
                parentStep.isComplete = parentStepIndex < parentIndex;
                if (parentStep.subSteps.length && subStepIndex >= 0) {
                    this.updateSubStepStates(parentStep, subStepIndex);
                }
            }
        });

        this.activeStepIndex = parentIndex;
        this.activeSubStepIndex = subStepIndex;

        this.checkForFirstAndLastStep();
    }

    /**
     * Clear any steps that have errored.
     * @private
     */
    private clearErroredSteps(): void {
        if (this.erroredSteps?.length) {
            for (const steps of this.erroredSteps) {
                const step: any = this.utilsService.getObjectValue(
                    this.wizardSteps, `[${steps.parentIndex}].subSteps[${steps.subStepIndex}].isErrored`
                );
                if (step) {
                    delete this.wizardSteps[steps.parentIndex].subSteps[steps.subStepIndex].isErrored;
                }
            }
        }

        this.erroredSteps = [];
    }

    /**
     * Helper function to check if the wizard is on the first or last step.
     * @private
     */
    private checkForFirstAndLastStep(): void {
        const subSteps: any = this.wizardSteps[this.activeStepIndex] && this.wizardSteps[this.activeStepIndex].subSteps;
        this.isFirstStep = this.isFirstStepCheck(this.wizardSteps, this.activeStepIndex, subSteps);
        this.isLastStep = this.isLastStepCheck(this.wizardSteps, this.activeStepIndex, subSteps);
    }

    /**
     * Check if the index is the first step for the given set of steps.
     * @param steps
     * @param index
     * @param subSteps
     * @private
     */
    private isFirstStepCheck(steps: any[], index: number, subSteps: any[]): boolean {
        const isFirstStep: boolean = steps && index <= steps.findIndex((item: any): any => item.isVisible);
        if (isFirstStep && (subSteps && subSteps.length)) {
            return subSteps && this.activeSubStepIndex <= subSteps.findIndex((item: any): any => item.isVisible);
        }

        return isFirstStep;
    }

    /**
     * Check if the index is the last step for a given set of steps.
     * @param steps
     * @param index
     * @param subSteps
     * @private
     */
    private isLastStepCheck(steps: any[], index: number, subSteps: any): boolean {
        const isLastStep: boolean = steps && index >= this.utilsService.findIndexReversed(steps, 'isVisible');
        if (isLastStep && (subSteps && subSteps.length)) {
            return subSteps && this.activeSubStepIndex >= this.utilsService.findIndexReversed(subSteps, 'isVisible');
        }

        return isLastStep;
    }

    /**
     * Is last step in section.
     * @param currentStep
     * @private
     */
    private isLastStepSection(currentStep: any): boolean {
        const visibleParentStepsIndex: number = this.utilsService.findIndexReversed(this.wizardSteps, 'isVisible');
        if (this.activeStepIndex < visibleParentStepsIndex) {
            if (!currentStep.subSteps || !currentStep.subSteps.length) {
                return true;
            } else {
                const visibleSubStepsIndex: number = this.utilsService.findIndexReversed(currentStep.subSteps, 'isVisible');

                return this.activeSubStepIndex === visibleSubStepsIndex;
            }
        } else {
            if (!currentStep.subSteps) {
                return this.activeStepIndex === visibleParentStepsIndex;
            } else {
                const visibleSubStepsIndex: number = this.utilsService.findIndexReversed(currentStep.subSteps, 'isVisible');

                return this.activeSubStepIndex === visibleSubStepsIndex;
            }
        }
    }

    /**
     * Set the active sub step index and then loop the parent step's sub steps and updates the isActive and isComplete states.
     * @param parentStep parent step object to be looped.
     * @param subStepIndex
     * @private
     */
    private updateSubStepStates(parentStep: { subSteps: any; }, subStepIndex: number): void {
        this.activeSubStepIndex = subStepIndex;
        parentStep.subSteps.forEach((subStep: {
            isVisible: any;
            isActive: boolean;
            isComplete: boolean;
        }, forIndex: number): void => {
            if (subStep.isVisible) {
                subStep.isActive = forIndex === subStepIndex;
                subStep.isComplete = forIndex < subStepIndex;
            }
        });
    }

    /**
     * Filter the template for visible steps and sub steps.
     * @param steps
     * @private
     */
    private filterVisibleSteps(steps: any[]): any[] {
        // Duplicate the steps, so we can manipulate them without updating the original.
        const duplicatedSteps: any = this.utilsService.duplicateObject(steps);

        return duplicatedSteps.filter((step: { isVisible: any; subSteps: any[]; }) => {
            if (step.isVisible) {
                if (step.subSteps && step.subSteps.length) {
                    const visibleSubSteps: any[] = step.subSteps.filter(subStep => subStep.isVisible);
                    if (visibleSubSteps.length) {
                        return step.subSteps = visibleSubSteps;
                    }
                } else {
                    return step;
                }
            }
        });
    }

    /**
     * Check to see if we should increment the sub step.
     * @param currentSubSteps
     * @private
     */
    private incrementSubStep(currentSubSteps: any): boolean {
        const lastVisibleSubStepIndex: number = this.utilsService.findIndexReversed(currentSubSteps, 'isVisible');

        return currentSubSteps && currentSubSteps.length > 1 &&
            lastVisibleSubStepIndex !== -1 &&
            this.activeSubStepIndex < lastVisibleSubStepIndex;
    }

    /**
     * Check to see if we should decrement the sub step.
     * @param currentSubSteps
     * @private
     */
    private decrementSubStep(currentSubSteps: any): boolean {
        const firstVisibleSubStepIndex: any = currentSubSteps.findIndex((item: {
            isVisible: any;
        }): any => item.isVisible);

        return currentSubSteps && currentSubSteps.length > 1 &&
            firstVisibleSubStepIndex !== -1 && this.activeSubStepIndex > firstVisibleSubStepIndex;
    }

    /**
     * Get next step index.
     * @param steps
     * @param activeIndex
     * @private
     */
    private getNextIndex(steps: any, activeIndex: number): number {
        const currentId: any = steps[activeIndex].id;

        return steps.findIndex(
            (item: {
                id: any;
                isVisible: any;
            }, index: number) => index >= activeIndex && item.id !== currentId && item.isVisible
        );
    }

    /**
     * Get previous step, sub step index.
     * @param previousStepIndex
     * @private
     */
    private getPreviousStepSubStepIndex(previousStepIndex: number): number | 'last' | 'first' {
        const previousStepSubSteps = this.wizardSteps[previousStepIndex].subSteps;
        const previousSubStepIndex = this.utilsService.findIndexReversed(previousStepSubSteps, 'isVisible');

        return previousSubStepIndex !== -1 ? previousSubStepIndex : 'last';
    }

    /**
     * Get next step, sub step index.
     * @param nextStepIndex
     * @private
     */
    private getNextStepSubStepIndex(nextStepIndex: number): number {
        const nextStepSubSteps = this.wizardSteps[nextStepIndex].subSteps;
        const nextSubStepIndex = nextStepSubSteps.findIndex((item: { isVisible: any; }) => item.isVisible);

        return nextSubStepIndex !== -1 ? nextSubStepIndex : 0;
    }

    /**
     * Get step by ID.
     * @param steps
     * @param id
     * @private
     */
    private getStepById(steps: any[], id: string): any {
        return steps.find(step => step.id === id);
    }

    /**
     * Remove redundant wizard modes when changing template.
     * @param selectedTemplate
     * @private
     */
    private removeRedundantModes(selectedTemplate: TemplateModel): any {
        const updatedWizardMode = [];
        for (const templateMode of selectedTemplate.wizardModes) {
            const mode = this.dropdown.wizardMode.find(item => item.key === templateMode.toLowerCase());
            updatedWizardMode.push(mode);
        }

        return updatedWizardMode;
    }
}
